import diamondHallmark from '@/shared/assets/images/diamond-hallmark.png';
import goldHallmark from '@/shared/assets/images/gold-hallmark.png';
import gsiHallmark from '@/shared/assets/images/gsi-hallmark.png';
import platinumHallmark from '@/shared/assets/images/platinum-hallmark.png';

export type PdpCertificationId = 'pgi' | 'bis' | 'igi' | 'gsi';

export interface PdpCertificationItem {
  id: PdpCertificationId;
  label: string;
  paragraphs: string[];
  image: string;
  imageClassName?: string;
  href: string;
}

export const PGI_CERTIFICATION: PdpCertificationItem = {
  id: 'pgi',
  label: 'PGI',
  image: platinumHallmark,
  href: 'https://platinumguild.com/',
  paragraphs: [
    'Platinum Guild International (PGI) was founded in 1975 and is a trade organisation supported by the major South African platinum producers. PGI has specialist teams dedicated to developing demand for platinum jewellery through consumer and trade facing programmes in the four key jewellery markets of China, India, Japan and USA.',
  ],
};

export const BIS_CERTIFICATION: PdpCertificationItem = {
  id: 'bis',
  label: 'BIS',
  image: goldHallmark,
  href: 'https://www.bis.gov.in/',
  paragraphs: [
    'The BIS hallmark is a system that provides certification of gold that indicates the purity of the metal. It authenticates that a piece of gold jewellery purchased, conforms to the standards as laid by the Bureau of Indian Standards which is the national standards organization in India.',
    'This hallmarking of gold jewellery was established in April 2000. It consists of a BIS logo, a 3 digit number that indicates the gold purity, the logo of the assaying center along with the logo or the code of the jeweller and the code that indicates the hallmarking date. This hallmarking has a high reputation among the jewellery industry in India.',
  ],
};

export const IGI_CERTIFICATION: PdpCertificationItem = {
  id: 'igi',
  label: 'IGI',
  image: diamondHallmark,
  href: 'https://www.igi.org/',
  paragraphs: [
    'The International Gemological Institute (IGI) is the largest independent lab for grading and evaluating diamonds and fine jewelry. It is renowned for its quality services, extensive experience and expertise. It is located in New York City, Mumbai, Kolkata, New Delhi, Surat, Los Angeles, Toronto, Hong Kong, Dubai, and Tokyo. IGI, founded in 1975, is the only international certification lab wholly owned and controlled by one central governing body, which ensures consistency in IGI reports across the globe.',
  ],
};

export const GSI_CERTIFICATION: PdpCertificationItem = {
  id: 'gsi',
  label: 'GSI',
  image: gsiHallmark,
  imageClassName: 'max-h-[31px] w-full',
  href: 'https://gemscience.net/',
  paragraphs: [
    "The Gemological Science International (GSI) is a system of certification that involves a comprehensive analysis of each piece of jewellery, including its metal content, gemstone quality, and workmanship. GSI's team of expert gemologists use advanced equipment and techniques to examine each jewellery component and provide an accurate assessment of its quality.",
    "GSI's diamond grading process is centred on its proprietary software. This state-of-the-art software reduces the chances of human error by automating the grading process, giving customers the assurance they need when purchasing fine jewellery.",
  ],
};

export interface PdpCertificationContext {
  metalType: string;
  diamondStoneType: string | null;
  solitaireTotalNoOfStones: number | null;
}

export function getPdpCertificationHeading(context: PdpCertificationContext): string {
  const isGoldOnly =
    context.metalType === 'Gold' &&
    !context.diamondStoneType &&
    !(context.solitaireTotalNoOfStones != null && context.solitaireTotalNoOfStones > 0);

  return isGoldOnly
    ? '100% Certified by Indian Govt. Standards'
    : '100% Certified by International Standards';
}

export function getVisiblePdpCertifications(
  context: PdpCertificationContext
): PdpCertificationItem[] {
  const items: PdpCertificationItem[] = [];

  if (context.metalType === 'Platinum') {
    items.push(PGI_CERTIFICATION);
  }

  if (context.metalType === 'Gold') {
    items.push(BIS_CERTIFICATION);
  }

  if (context.diamondStoneType != null) {
    items.push(IGI_CERTIFICATION);
  }

  const hasStoneCertification =
    context.diamondStoneType != null ||
    (context.solitaireTotalNoOfStones != null && context.solitaireTotalNoOfStones > 0);

  if (hasStoneCertification) {
    items.push(GSI_CERTIFICATION);
  }

  return items;
}
